// configShadowFindings.js — the analyst-facing vocabulary for the
// `security.config_shadow` signal: what each finding is called, what the tag's
// value JSON means, and why the signal fired.
//
// The signal itself emits only a tag name plus
// `{layer, source_path, writer, operation, exists, otel_keys?}` — no prose, no
// severity, no precedence context. Turning that into something an analyst can
// act on needs the detection's own logic (backend/signals-catalog/config_shadow/
// signal.py) and its alerting dossiers (docs/signals-ads/ads-*.md) restated here.
// Kept in one module so the center-pane table and the right-hand panel can't
// describe the same finding differently.

// Grouping key for collapsing repeated observations of the SAME problem. The tag
// value carries no config_target_id, but (layer, source_path) identifies the
// target — config_target_id is itself derived from agent+layer+scope — so this is
// the target identity as seen from a tag.
export function findingGroupKey(tag, detail) {
    // JSON.stringify rather than joining on a separator: a source path can
    // contain any character, so a literal delimiter risks two different targets
    // colliding on one key. Array encoding is collision-free and keeps this file
    // plain text — an earlier version used a raw NUL as the separator, which made
    // git treat the whole file as binary and hid it from diff review.
    return JSON.stringify([tag, detail?.layer ?? "", detail?.source_path ?? ""]);
}

export const FINDING_LABELS = {
    managed_settings_shadowed: "Managed settings shadowed",
    otel_keys_overwritten: "Telemetry keys overwritten",
    managed_settings_written_by_non_origin: "Managed settings written by non-Origin process",
    // session_guardrail_bypass (GitHub Copilot CLI).
    launched_with_guardrails_off: "Launched with guardrails off",
    switched_to_allow_all: "Switched to allow-all in session",
};

export function findingLabel(tag) {
    return FINDING_LABELS[tag] || tag || "finding";
}

// Ceiling on raw `signal_tags` rows one endpoint-firing read pulls back, and so
// the only bound on the grouping and rollup below — display truncation slices
// their output, not their input. Consumed as `getSignalEndpointFirings`' default
// `limit`; it lives here, beside the work it bounds, so a node-environment test
// can pin the cost against it without pulling in apiClient's DOM-dependent graph.
export const ENDPOINT_FIRING_ROW_LIMIT = 20000;

// Second aggregation level: fold per-TARGET groups into per-KIND rollups.
//
// The two levels differ in what bounds them. A kind is what an analyst triages,
// and the signal declares a fixed, tiny set. A target — (layer, source_path) — is
// what they then investigate, and its cardinality is the fleet's to set: a policy
// file whose observed path varies per write yields a fresh target every time. So a
// surface rendering one element per kind stays bounded and one per target does not.
//
// `targets` stays attached rather than reduced to a count because the drill-down
// needs the rows, and deriving both levels in one pass keeps them consistent.
//
// Cost: walks and sorts every group, because a badge's counts have to total what
// exists rather than what fits. Bounded only by ENDPOINT_FIRING_ROW_LIMIT, worst
// case one group per row. Not top-K — at that ceiling the sort is immaterial, and
// a partial selection would still have to visit every group to total the counts.
export function rollupFindingKinds(groups = []) {
    const kinds = new Map();
    for (const g of groups) {
        let k = kinds.get(g.tag);
        if (!k) {
            k = {
                tag: g.tag,
                targets: [],
                observations: 0,
                firstSeenMs: null,
                lastSeenMs: null,
                writers: [],
                operations: [],
                layers: [],
            };
            kinds.set(g.tag, k);
        }
        k.targets.push(g);
        // A group's `count` is its observation count; missing means one row.
        k.observations += Number.isFinite(g.count) ? g.count : 1;
        if (Number.isFinite(g.lastSeenMs) && (k.lastSeenMs === null || g.lastSeenMs > k.lastSeenMs)) {
            k.lastSeenMs = g.lastSeenMs;
        }
        if (Number.isFinite(g.firstSeenMs) && (k.firstSeenMs === null || g.firstSeenMs < k.firstSeenMs)) {
            k.firstSeenMs = g.firstSeenMs;
        }
        for (const w of g.writers || []) if (w && !k.writers.includes(w)) k.writers.push(w);
        for (const op of g.operations || []) if (op && !k.operations.includes(op)) k.operations.push(op);
        if (g.layer && !k.layers.includes(g.layer)) k.layers.push(g.layer);
    }
    for (const k of kinds.values()) {
        k.targets.sort((a, b) => (b.lastSeenMs ?? 0) - (a.lastSeenMs ?? 0));
    }
    // Newest kind first — the ranking an analyst triages by — with observation
    // volume as the tiebreak so an undated kind still lands somewhere stable.
    return [...kinds.values()].sort(
        (a, b) => (b.lastSeenMs ?? 0) - (a.lastSeenMs ?? 0) || b.observations - a.observations,
    );
}

// One kind's scan line: what fired, across how many targets, seen how often.
// Targets and observations are only shown when they add information — a single
// target observed once needs neither, and printing "1 target · 1×" on every row
// is the same noise in smaller type.
export function kindSummary(kind) {
    if (!kind) return "";
    const parts = [findingLabel(kind.tag)];
    const targets = kind.targets?.length ?? 0;
    if (targets > 1) parts.push(`${targets} targets`);
    if (kind.observations > Math.max(targets, 1)) parts.push(`${kind.observations}×`);
    return parts.join(" · ");
}

// A finding's `value` is backend-authored JSON, versioned independently of this
// view. Parse defensively: a shape change must degrade to "no detail" rather than
// blank the row.
export function parseFinding(value) {
    if (!value) return null;
    try {
        const v = JSON.parse(value);
        return v && typeof v === "object" ? v : null;
    } catch {
        return null;
    }
}

// The signal's own sentinel for a write it couldn't pin to a process. Render as
// unknown — never as a path, and never implying Origin wrote it.
export const UNATTRIBUTED = "unattributed";

export function isUnattributed(writer) {
    return !writer || writer === UNATTRIBUTED;
}

export function writerLabel(writer) {
    return isUnattributed(writer) ? UNATTRIBUTED : writer;
}

// Basename of a writer path, for a compact cell. Keeps the sentinel intact.
export function writerShort(writer) {
    if (isUnattributed(writer)) return UNATTRIBUTED;
    return String(writer).split(/[/\\]/).filter(Boolean).pop() || String(writer);
}

// The same settings layer reaches this view spelled two different ways: a signal
// tag carries the serde form (`ManagedFile`, `RemoteManaged`) while
// `endpoint_config_state_current.layer` stores the proto enum name
// (`SETTINGS_LAYER_MANAGED_FILE`, `SETTINGS_LAYER_REMOTE_MANAGED`). Comparing them
// raw silently never matches, which leaves a finding with no config target and no
// body to show. Fold both to one key before comparing.
//
// Path alone is NOT a substitute: `USER` and `PROJECT` can both point at
// `~/.claude/settings.json`, so dropping the layer would match the wrong target.
export function normalizeLayer(raw) {
    return String(raw ?? "")
        .replace(/^SETTINGS_LAYER_/i, "")
        .replace(/_/g, "")
        .toLowerCase();
}

// True when a tag's layer and a config-state row's layer are the same layer.
export function sameLayer(a, b) {
    const na = normalizeLayer(a);
    return na !== "" && na === normalizeLayer(b);
}

// Human form of a layer, whichever spelling arrived. `SETTINGS_LAYER_MANAGED_FILE`
// → `ManagedFile`; an already-serde value passes through.
export function layerDisplay(raw) {
    const s = String(raw ?? "");
    if (!s) return "";
    if (!/^SETTINGS_LAYER_/i.test(s)) return s;
    return s
        .replace(/^SETTINGS_LAYER_/i, "")
        .split("_")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join("");
}

// Config-state rows carry the proto enum names for the writer operation and the
// observation source (`CONFIG_WRITER_OPERATION_WRITE`,
// `CONFIG_OBSERVATION_SOURCE_STARTUP_SCAN`), while a signal tag carries the short
// form (`write`). Render the short form everywhere — the enum name is noise in a
// timeline an analyst is scanning.
export function operationDisplay(raw) {
    const s = String(raw ?? "");
    if (!s) return "";
    return s.replace(/^CONFIG_WRITER_OPERATION_/i, "").replace(/_/g, " ").toLowerCase();
}

export function observationSourceDisplay(raw) {
    const s = String(raw ?? "");
    if (!s) return "";
    const stem = s.replace(/^CONFIG_OBSERVATION_SOURCE_/i, "").replace(/_/g, " ").toLowerCase();
    return stem === "unspecified" ? "observation" : stem;
}

// Claude's policy-source resolution order, highest first. `policySettings` is
// first-NON-EMPTY-wins: the top two layers are guarded by a non-empty check and
// fall through when empty, while the managed file is a bare truthy check (so an
// empty managed file still wins). Origin owns the ManagedFile tier.
export const POLICY_PRECEDENCE = [
    { layer: "RemoteManaged", note: "remote managed settings (a sync cache Claude Code writes, not an admin artifact)" },
    { layer: "MdmAdmin", note: "MDM: macOS preference domain or Windows HKLM" },
    { layer: "ManagedFile", note: "file-based managed settings owned by Origin" },
    { layer: "MdmUser", note: "HKCU (lowest tier)" },
];

// The telemetry keys Origin writes into managed-settings.json's `env` block,
// split by what losing each one costs. Mirrors `_ORIGIN_OTEL_KEYS` /
// `_OTEL_OVERRIDE_KEYS` in the signal; a key absent here still renders, it just
// gets no impact sentence.
const ATTRIBUTION_KEYS = new Set([
    "OTEL_METRICS_INCLUDE_VERSION",
]);
const TRANSPORT_KEYS = new Set([
    "CLAUDE_CODE_ENABLE_TELEMETRY",
    "CLAUDE_CODE_ENHANCED_TELEMETRY_BETA",
    "OTEL_METRICS_EXPORTER",
    "OTEL_METRICS_INCLUDE_VERSION",
    "OTEL_LOGS_EXPORTER",
    "OTEL_TRACES_EXPORTER",
    "OTEL_EXPORTER_OTLP_PROTOCOL",
    "OTEL_EXPORTER_OTLP_ENDPOINT",
    "OTEL_EXPORTER_OTLP_CLIENT_CERTIFICATE",
    "OTEL_EXPORTER_OTLP_CLIENT_KEY",
]);
const CONTENT_SCOPE_KEYS = new Set([
    "OTEL_LOG_USER_PROMPTS",
    "OTEL_LOG_ASSISTANT_RESPONSES",
    "OTEL_LOG_RAW_API_BODIES",
    "OTEL_LOG_TOOL_CONTENT",
    "OTEL_LOG_TOOL_DETAILS",
]);
// Per-signal overrides Origin never writes. OTLP resolves these AHEAD of the base
// endpoint, so adding one redirects a stream while the key Origin owns still
// reads as the loopback collector — the quietest of the three classes.
const OVERRIDE_KEYS = new Set([
    "OTEL_EXPORTER_OTLP_HEADERS",
    "OTEL_EXPORTER_OTLP_METRICS_ENDPOINT",
    "OTEL_EXPORTER_OTLP_LOGS_ENDPOINT",
]);

export const ALL_OTEL_KEYS = new Set([
    ...ATTRIBUTION_KEYS,
    ...CONTENT_SCOPE_KEYS,
    ...OVERRIDE_KEYS,
    ...TRANSPORT_KEYS,
]);

// GitHub Copilot: Origin's capture is the `telemetry` block of
// GitHubCopilot/managed-settings.json, and the signal reports JSON pointers cut to
// the block's direct child (`_copilot_telemetry_modified`). Transport and the two
// content keys are the ones Origin writes (`agent/data/src/copilot_otel_spec.rs`
// TELEMETRY_POLICY_SPEC). `capture` is not written by Origin but is content: its
// per-class fields override `captureContent` for the same data (CLI 1.0.93
// schema), so a `capture` object changes what content Origin receives. The
// block's other keys render as "other". A removed block arrives as `/telemetry`.
const COPILOT_TELEMETRY_POINTER = "/telemetry";
const COPILOT_TRANSPORT_POINTERS = new Set([
    COPILOT_TELEMETRY_POINTER,
    "/telemetry/enabled",
    "/telemetry/endpoint",
    "/telemetry/protocol",
]);
const COPILOT_CONTENT_POINTERS = new Set([
    "/telemetry/captureContent",
    "/telemetry/lockCaptureContent",
    "/telemetry/capture",
]);

// Copilot's policy file, by the same tail rule as the signal
// (`_COPILOT_POLICY_TAIL`): Windows `GitHubCopilot\managed-settings.json`, macOS
// `GitHubCopilot/managed-settings.json`, case-insensitive.
function isCopilotPolicyPath(path) {
    return /(^|[\\/])githubcopilot[\\/]managed-settings\.json$/i.test(String(path ?? ""));
}

// A Copilot policy value under a loaded user hive. The agent reports it as
// `registry://HKU/<sid>/SOFTWARE/Policies/GitHubCopilot`
// (`agent/data/src/copilot_native_policy.rs`). VS Code reads the user hive
// below HKLM; the Copilot CLI reads HKLM only, so this source shadows the file
// for VS Code and leaves it in force for the CLI.
function isCopilotUserHivePath(path) {
    return /^registry:\/\/HKU\/[^/]+\/SOFTWARE\/Policies\/GitHubCopilot$/i.test(String(path ?? ""));
}

// The unforced com.github.copilot domain as VS Code reads it for one macOS
// user, polled as `MdmUser` (`agent/data/src/copilot_native_policy.rs`
// `read_unforced_preference_layers`): the user's own
// `~/Library/Preferences/com.github.copilot.plist` over the device-wide
// `/Library/Preferences/com.github.copilot.plist`. VS Code reads it as native
// policy above the policy file without a forced check; the Copilot CLI reads
// forced values only. A standard user writes their own file with `defaults
// write`; the device-wide file needs an administrator.
function isCopilotUserPreference(detail) {
    return normalizeLayer(detail?.layer) === "mdmuser"
        && /(^|[\\/])Library[\\/]Preferences[\\/]com\.github\.copilot\.plist$/i.test(String(detail?.source_path ?? ""));
}

// A native Copilot policy source both clients read: the HKLM key or a
// com.github.copilot managed preference (device or per user). Both Copilot
// clients merge native values with the policy file key by key (DESIGN decision
// 21), so the copy names the keys the source sets rather than calling the
// whole file overridden.
function isCopilotMachinePolicyPath(path) {
    const s = String(path ?? "");
    return /^registry:\/\/HKLM\/SOFTWARE\/Policies\/GitHubCopilot$/i.test(s)
        || /(^|[\\/])com\.github\.copilot\.plist$/i.test(s);
}

// VS Code's cached copy of GitHub's server-managed Copilot settings, the
// application-storage database of a VS Code user data directory
// (`agent/data/src/copilot_server_policy.rs`). VS Code ranks it over the
// policy file key by key; the Copilot CLI ranks its server channel below the
// file, so the file stays in force for the CLI.
function isCopilotServerCachePath(detail) {
    return normalizeLayer(detail?.layer) === "remotemanaged"
        && /(^|[\\/])User[\\/]globalStorage[\\/]state\.vscdb$/i.test(String(detail?.source_path ?? ""));
}

// "sets model and sandbox" from the signal's `native_keys`, or a generic
// phrase when the detail carries none (unreadable content).
function nativeKeysPhrase(detail) {
    const keys = Array.isArray(detail?.native_keys) ? detail.native_keys.filter((k) => typeof k === "string" && k) : [];
    if (keys.length === 0) return "sets values";
    if (keys.length === 1) return `sets ${keys[0]}`;
    if (keys.length === 2) return `sets ${keys[0]} and ${keys[1]}`;
    return `sets ${keys.slice(0, -1).join(", ")} and ${keys[keys.length - 1]}`;
}

export function otelKeyClass(key) {
    if (ATTRIBUTION_KEYS.has(key)) return "attribution";
    if (OVERRIDE_KEYS.has(key)) return "override";
    if (CONTENT_SCOPE_KEYS.has(key) || COPILOT_CONTENT_POINTERS.has(key)) return "content";
    if (TRANSPORT_KEYS.has(key) || COPILOT_TRANSPORT_POINTERS.has(key)) return "transport";
    return "other";
}

const OTEL_CLASS_IMPACT = {
    attribution: "controls client-build attribution; clearing it removes the emitting Claude Code version from telemetry",
    content: "controls exported content; clearing it can leave prompt bodies empty or omit response bodies while telemetry volume appears normal",
    override: "overrides the base endpoint for one signal and can redirect its stream",
    other: "not a key Origin manages",
    transport: "controls telemetry export; clearing or changing it can stop or redirect the prompt stream",
};

export function otelKeyImpact(key) {
    return OTEL_CLASS_IMPACT[otelKeyClass(key)];
}

// A layer named in a way a reader who has never seen Claude's settings stack can
// follow. The raw values (`ManagedFile`, `RemoteManaged`) are internal enum labels
// — dropping them into prose reads as jargon, and worse, as though the reader
// already knows what tier that is. Describe the tier instead; the exact label is
// still shown in the Layer field beside the prose.
const LAYER_DESCRIPTION = {
    managedfile: "the managed policy file",
    manageddropin: "a managed policy drop-in",
    remotemanaged: "a remote-managed policy source",
    mdmadmin: "an MDM-delivered policy source",
    mdmuser: "a per-user policy source",
    // Codex CLI: the system requirements.toml Origin writes. For Codex the
    // `managedfile` label above is the legacy managed_config.toml, which Codex
    // composes above the requirements file.
    requirements: "the requirements file",
};

export function layerDescription(raw) {
    return LAYER_DESCRIPTION[normalizeLayer(raw)] || "a policy source";
}

// The descriptions are lowercase so they read correctly mid-sentence; capitalize
// when one has to lead a sentence.
function sentenceCase(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// How to name the writing process in a sentence. Unattributed is a real outcome —
// startup scans and MDM polls carry no writer — so say so rather than implying
// Origin, or inventing a process.
function writerPhrase(writer) {
    return isUnattributed(writer)
        ? "A process other than the Origin agent"
        : `A process other than the Origin agent (${writerShort(writer)})`;
}

function atPath(detail) {
    const p = detail?.source_path;
    return p ? ` at ${p}` : "";
}

// Why this tag fired, in the reader's terms. Derived from the exact conditions in
// signal.py — the consequence, not a restatement of the tag name. Each ends by
// stating whether the telemetry Origin depends on was affected, because that is
// the difference between "visibility loss" and "someone touched a file".
export function whyItFired(tag, detail) {
    const writer = detail?.writer;
    switch (tag) {
        case "managed_settings_shadowed":
            if (isCopilotUserHivePath(detail?.source_path)) {
                return `${sentenceCase(layerDescription(detail?.layer))}${atPath(detail)} ${nativeKeysPhrase(detail)} that VS Code reads ahead of the policy file Origin writes for GitHub Copilot, key by key; the other keys in the file stay in force. The Copilot CLI reads the machine key and not this per-user hive, so the policy file is still in force for the CLI. The endpoint's managed-config state shows SHADOWED when one of these keys is a key Origin's policy sets.`;
            }
            if (isCopilotUserPreference(detail)) {
                return `An unforced preference for this Mac's user${atPath(detail)} ${nativeKeysPhrase(detail)}, which VS Code reads as native policy ahead of the policy file Origin writes for GitHub Copilot, key by key; the other keys in the file stay in force. It is the user's own preference over the device-wide file /Library/Preferences/com.github.copilot.plist. The user's own needs no administrator rights to write (\`defaults write com.github.copilot\`); the device-wide file does. The Copilot CLI reads only forced values, so the policy file is still in force for the CLI. A telemetry value here replaces Origin's capture settings in VS Code. A forced value for the same key, from an MDM profile, outranks it. The endpoint's managed-config state shows SHADOWED when one of these keys is a key Origin's policy sets.`;
            }
            if (isCopilotServerCachePath(detail)) {
                return `VS Code's cached copy of GitHub's server-managed Copilot settings${atPath(detail)} ${nativeKeysPhrase(detail)}, which VS Code reads ahead of the policy file Origin writes, key by key; the other keys in the file stay in force. The Copilot CLI reads its server settings below the policy file, so the file is still in force for the CLI. A telemetry value here replaces Origin's capture settings in VS Code. The endpoint's managed-config state shows SHADOWED when one of these keys is a key Origin's policy sets.`;
            }
            if (isCopilotMachinePolicyPath(detail?.source_path)) {
                return `${sentenceCase(layerDescription(detail?.layer))}${atPath(detail)} ${nativeKeysPhrase(detail)} that the Copilot CLI and VS Code read ahead of the policy file Origin writes, key by key; the other keys in the file stay in force. A native telemetry value switches off Origin's Copilot capture. The endpoint's managed-config state shows SHADOWED when one of these keys is a key Origin's policy sets.`;
            }
            return `${sentenceCase(layerDescription(detail?.layer))}${atPath(detail)} out-ranks the policy file Origin writes. The AI tool reads this source first. Check its contents to see whether Origin's policy, and for Claude its telemetry, is still in force.`;
        case "otel_keys_overwritten":
            return `${writerPhrase(writer)} changed telemetry settings inside ${layerDescription(detail?.layer)}${atPath(detail)}. This affected the activity Origin can receive from the endpoint. Values are omitted because they may contain credentials.`;
        case "managed_settings_written_by_non_origin":
            // The Copilot CLI validates this file whole against its schema: one
            // value of the wrong type and the CLI keeps only its sandbox floor,
            // which drops Origin's telemetry block though the block's own keys did
            // not change. An unknown key is ignored (CLI 1.0.93, measured
            // 2026-10-14); VS Code validates neither.
            if (isCopilotPolicyPath(detail?.source_path)) {
                return `${writerPhrase(writer)} wrote ${layerDescription(detail?.layer)}${atPath(detail)}. The AI tool trusts this file based on its location. The telemetry block was unchanged, but the Copilot CLI ignores the whole file, telemetry included, if this write set a value of the wrong type. Check that Origin still receives Copilot CLI activity.`;
            }
            return `${writerPhrase(writer)} wrote ${layerDescription(detail?.layer)}${atPath(detail)}. The AI tool trusts this file based on its location. Origin telemetry settings were unchanged, but the write may have changed other settings.`;
        default:
            return "";
    }
}

// What the analyst should look for next, per finding. Condensed from the
// per-finding ADS dossiers' Response sections.
export function whatToCheck(tag) {
    switch (tag) {
        case "managed_settings_shadowed":
            return [
                "Check whether the shadowing source defines an env block or disables or redirects telemetry.",
                "Identify the writer. The AI tool's own binary indicates a vendor-console push; another writer may indicate tampering.",
                "Confirm that Origin telemetry continued after this timestamp. A source that remains unchanged across observations may be inactive.",
            ];
        case "otel_keys_overwritten":
            return [
                "Compare the current content with the change timeline. Classify the change as disabled, narrowed, or redirected.",
                "Check prompt volume after the write. Empty prompt bodies can indicate a content-scope change.",
                "Treat an exporter redirected away from loopback as possible prompt exfiltration.",
            ];
        case "managed_settings_written_by_non_origin":
            return [
                "Classify the change as weaker enforcement, a new execution path, or a cosmetic update.",
                "If an execution surface was added, treat the declared binary as untrusted and pivot to process telemetry.",
                "Check whether Origin reverted the change and whether the same write appears on other endpoints.",
            ];
        default:
            return [];
    }
}

// Parse one pre-rendered agent-side diff entry into structure. The agent emits
// strings, not structured data (`agent/session_enricher/src/config_diff.rs`):
//   added / removed  →  "<pointer> = <value>"
//   updated          →  "<pointer>: <before> → <after>"
// Pointers are RFC 6901. A whole added/removed subtree collapses to one root
// entry, so `pointer` can name an object rather than a leaf. Returns the raw
// string as `pointer` when it matches neither shape, so nothing is dropped.
export function parseDiffEntry(entry, kind) {
    const s = String(entry ?? "");
    if (kind === "updated") {
        const sep = s.indexOf(": ");
        if (sep > 0) {
            const pointer = s.slice(0, sep);
            const rest = s.slice(sep + 2);
            const arrow = rest.indexOf(" → ");
            if (arrow >= 0) {
                return { pointer, before: rest.slice(0, arrow), after: rest.slice(arrow + 3), kind };
            }
            return { pointer, after: rest, kind };
        }
        return { pointer: s, kind };
    }
    const eq = s.indexOf(" = ");
    if (eq > 0) {
        const value = s.slice(eq + 3);
        return kind === "removed"
            ? { pointer: s.slice(0, eq), before: value, kind }
            : { pointer: s.slice(0, eq), after: value, kind };
    }
    return { pointer: s, kind };
}

// Does this diff pointer touch a key Origin's telemetry depends on? The diff is
// rendered text and a subtree can collapse to one entry (`/env = {...}`), so this
// is a substring test over the whole entry — the same reason the signal itself
// substring-matches rather than comparing key paths.
//
// Copilot's keys are pointers, not env names, so they match on the entry's own
// pointer instead: `/telemetry` or anything under it, cut to the block's child as
// the signal does. The pointer is the first token, whether the caller passes the
// rendered entry (`/telemetry/endpoint: a <arrow> b`, the signal's updated form)
// or `pointer before after`.
export function touchedOtelKeys(entryText) {
    const s = String(entryText ?? "");
    const keys = [...ALL_OTEL_KEYS].filter((k) => s.includes(k));
    const pointer = s.split(/\s/)[0].replace(/:$/, "");
    if (pointer === COPILOT_TELEMETRY_POINTER || pointer.startsWith(`${COPILOT_TELEMETRY_POINTER}/`)) {
        keys.push(pointer.split("/").slice(0, 3).join("/"));
    }
    return keys;
}
